import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnChanges,
  OnDestroy,
  SimpleChanges,
  HostListener,
  ChangeDetectionStrategy,
  ElementRef,
  ViewChild,
  inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { CustomDatePickerComponent } from '../../../components/custom-date-picker/custom-date-picker.component';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { DateRange } from '../date-range-picker/date-range-picker.component';
import { RewashSettingsComponent } from '../rewash-settings/rewash-settings.component';
import { FamilyPlanSettingsComponent } from '../family-plan-settings/family-plan-settings.component';
import { ModalFooterActionsComponent } from '../modal-footer-actions/modal-footer-actions.component';
import {
  CheckboxMultiDropdownComponent,
  CheckboxMultiDropdownFooterAction,
  CheckboxMultiDropdownOption,
} from '../checkbox-multi-dropdown/checkbox-multi-dropdown.component';
import { CurrencyActionInputComponent } from '../currency-action-input/currency-action-input.component';
import { DropdownToggleHelper } from '../../helpers/dropdown-toggle.helper';

export interface FilterCategory {
  id: string;
  name: string;
  type?: 'checkbox' | 'dropdown' | 'radio' | 'multi-dropdown' | 'amount-range' | 'date-range';
  options: FilterOption[];
}

export interface FilterOption {
  id: string;
  label: string;
  value: string;
  selected: boolean;
}

export interface FilterModalCustomSortAction {
  action: 'rename' | 'delete';
  sortOrderId: string;
  /** Previous display label (rename) or item label (delete). */
  label: string;
  /** Inline rename: new name to persist (omit for delete). */
  newLabel?: string;
}

export interface FilterModalCustomRangeConfig {
  categoryId: string;
  triggerValue?: string;
  startLabel?: string;
  endLabel?: string;
  startPlaceholder?: string;
  endPlaceholder?: string;
  startDate: Date | null;
  endDate: Date | null;
  /** Latest selectable day — used to keep a "created date" range out of the future. */
  maxDate?: Date | null;
}

/** Min/max currency range shown for an `amount-range` filter category. */
export interface FilterModalAmountRangeConfig {
  categoryId: string;
  minLabel?: string;
  maxLabel?: string;
  minPlaceholder?: string;
  maxPlaceholder?: string;
  minAmount: number | null;
  maxAmount: number | null;
}

export interface FilterModalAmountRange {
  minAmount: number | null;
  maxAmount: number | null;
}

@Component({
  selector: 'app-filter-modal',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatDatepickerModule,
    RewashSettingsComponent,
    FamilyPlanSettingsComponent,
    ModalFooterActionsComponent,
    CheckboxMultiDropdownComponent,
    CurrencyActionInputComponent,
    CustomDatePickerComponent,
  ],
  templateUrl: './filter-modal.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./filter-modal.component.scss'],
})
export class FilterModalComponent implements OnChanges, OnDestroy {
  private readonly host = inject(ElementRef<HTMLElement>);
  /** Shared overlay registry id for the single-select filter dropdown (body portal). */
  private readonly filterDropdownOverlayId = 'filter-modal-dropdown';

  @Input() isVisible = false;
  @Input() header = 'Filter';
  @Input() primaryButtonText = 'Apply';
  @Input() secondaryButtonText = 'Clear';
  @Input() categories: FilterCategory[] = [];
  @Input() customClass?: string;
  @Input() customRangeConfig?: FilterModalCustomRangeConfig | null;
  /** Min/max amount inputs for `amount-range` filter categories. */
  @Input() amountRangeConfig?: FilterModalAmountRangeConfig | null;
  /** When true, shows the drag/reorder action under filter categories (e.g. Sort on Packages / Sales). */
  @Input() showMostUsedServices = false;
  /** Label for the action that opens the drag-and-drop (most-used) flow. */
  @Input() mostUsedServicesButtonText = 'Drag and drop';
  /** If this radio value is clicked, open drag-and-drop modal immediately. */
  @Input() triggerMostUsedOnOptionValue: string | null = null;
  /** Shows loading state while parent fetches dynamic sort options. */
  @Input() isLoading = false;
  /** Sales panel Sort modal: location list for the dropdown above Sort By. */
  @Input() sortPanelLocations: { id: string; label: string }[] = [];
  /** Bound selected location id (`null` = placeholder “Select location”). */
  @Input() sortPanelSelectedLocationId: string | null = null;
  /** Configure Sales Panel: show admin rewash window settings (admin-scoped, not location-scoped). */
  @Input() showRewashSettings = false;
  /** Configure Sales Panel: show the Family Plan section (scoped to the selected location). */
  @Input() showFamilyPlanSettings = false;
  @ViewChild(FamilyPlanSettingsComponent) familyPlanSettings?: FamilyPlanSettingsComponent;

  /** Same staged-load contract as `isRewashLoading`, for the Family Plan section. */
  isFamilyLoading = false;
  /** Unsaved family plan edits; Apply saves them. */
  isFamilyDirty = false;

  /**
   * True while the mounted rewash section is still fetching. Seeded to `showRewashSettings`
   * the moment the panel opens so the first change detection pass already knows a second
   * fetch is outstanding -- the child's `false` arrives later, off the mounting pass.
   */
  isRewashLoading = false;

  /**
   * The panel paints only once every section it owns has its data (CWS-598). With
   * `showRewashSettings` false this is exactly `!isLoading`, so other hosts of this modal
   * (orders filter, reports) are unaffected.
   */
  get isPanelReady(): boolean {
    return (
      !this.isLoading &&
      !(this.showRewashSettings && this.isRewashLoading) &&
      !(this.showFamilyPlanSettings && this.isFamilyLoading)
    );
  }

  /**
   * The shimmer now covers the rewash fetch too, so naming only the sort options would be
   * wrong -- but hosts without that section still see the original wording.
   */
  get loadingLabel(): string {
    return this.showRewashSettings ? 'Loading...' : 'Loading sort options...';
  }

  onRewashLoadingChange(loading: boolean): void {
    this.isRewashLoading = loading;
  }

  onFamilyLoadingChange(loading: boolean): void {
    this.isFamilyLoading = loading;
  }

  onFamilyDirtyChange(dirty: boolean): void {
    this.isFamilyDirty = dirty;
  }

  /** `carWashTypes` on configure-sales / package Sort modals (layout is independent of `showMostUsedServices`). */
  static readonly CAR_WASH_CATEGORY_ID = 'carWashTypes';

  /** Same Clear footer as Create Discount Location multi-select. */
  readonly multiDropdownClearAction: CheckboxMultiDropdownFooterAction = { label: 'Clear' };
  /** Figma form checkbox size used by Create Discount Location. */
  readonly multiDropdownCheckboxSize = 22;

  @Output() close = new EventEmitter<void>();
  @Output() apply = new EventEmitter<FilterCategory[]>();
  @Output() clear = new EventEmitter<void>();
  @Output() customRangeChange = new EventEmitter<DateRange>();
  @Output() amountRangeChange = new EventEmitter<FilterModalAmountRange>();
  @Output() mostUsedServices = new EventEmitter<void>();
  @Output() mostUsedServicesDefaults = new EventEmitter<void>();
  @Output() customSortAction = new EventEmitter<FilterModalCustomSortAction>();
  @Output() sortPanelSelectedLocationIdChange = new EventEmitter<string | null>();
  /** Emitted after a checkbox/radio option toggles so parents can cascade dependent categories. */
  @Output() optionChange = new EventEmitter<{
    categoryId: string;
    optionId: string;
    categories: FilterCategory[];
  }>();

  isClosing = false;
  openDropdownId: string | null = null;
  /** True after fixed-overlay top/left have been applied (prevents top-left flash). */
  dropdownOverlayReady = false;
  openCustomSortMenuValue: string | null = null;
  /** `option.value` while inline rename is active (custom-order:* only). */
  renameEditingOptionValue: string | null = null;
  renameDraft = '';
  renameInlineError = '';
  /** Themed location dropdown (Sort modal), same interaction pattern as notification modal. */
  isSortPanelLocationDropdownOpen = false;

  private overlayMenuEl: HTMLElement | null = null;
  private menuHomeParent: HTMLElement | null = null;
  private menuHomeNextSibling: ChildNode | null = null;
  private overlayScrollParents: EventTarget[] = [];
  private readonly onOverlayReposition = (): void => {
    if (this.openDropdownId) {
      this.applyDropdownMenuPosition(this.openDropdownId);
    }
  };

  get usePackageSortLayout(): boolean {
    return (
      this.sortPanelLocations.length > 0 ||
      this.categories.some(c => c.id === FilterModalComponent.CAR_WASH_CATEGORY_ID) ||
      this.categories.some(c => c.id === 'sortBy')
    );
  }

  get carWashCategory(): FilterCategory | undefined {
    return this.categories.find(c => c.id === FilterModalComponent.CAR_WASH_CATEGORY_ID);
  }

  get mainCategoriesForLayout(): FilterCategory[] {
    if (this.usePackageSortLayout) {
      return this.categories.filter(c => c.id !== FilterModalComponent.CAR_WASH_CATEGORY_ID);
    }
    return this.categories;
  }

  /** Sort modal: actions need a location when the dropdown is shown. */
  get isSortPanelLocationMissing(): boolean {
    return (
      this.sortPanelLocations.length > 0 &&
      (this.sortPanelSelectedLocationId == null ||
        String(this.sortPanelSelectedLocationId).trim() === '')
    );
  }

  get isSortPanelApplyDisabled(): boolean {
    return this.getSelectedCount() === 0 || this.isSortPanelLocationMissing;
  }

  /**
   * Apply stays live at zero selections, because applying an empty selection is how a cleared
   * filter reaches the table -- Clear only resets what the popup is holding (CWS-509). The Sort
   * panel is the exception: there is no sorting by nothing. A half-open date range is still
   * incomplete either way.
   */
  get isApplyDisabled(): boolean {
    if (!this.isPanelReady) {
      return true;
    }
    const disabled = this.sortPanelLocations.length
      ? this.isSortPanelApplyDisabled
      : this.hasIncompleteDateRange();
    // Unsaved Family Plan edits are applied by this button, whatever the sort selection is.
    return disabled && !this.isFamilyDirty;
  }

  onSortPanelLocationIdChange(id: string | null): void {
    this.sortPanelSelectedLocationIdChange.emit(id);
  }

  getSortPanelSelectedLocationLabel(): string {
    const id = this.sortPanelSelectedLocationId?.trim();
    if (!id) {
      return 'Select location';
    }
    const hit = this.sortPanelLocations.find(l => l.id === id);
    return hit?.label?.trim() || 'Select location';
  }

  toggleSortPanelLocationDropdown(event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    if (this.isLoading || !this.sortPanelLocations.length) {
      return;
    }
    this.isSortPanelLocationDropdownOpen = !this.isSortPanelLocationDropdownOpen;
  }

  closeSortPanelLocationDropdown(): void {
    this.isSortPanelLocationDropdownOpen = false;
  }

  selectSortPanelLocation(loc: { id: string; label: string }, event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    this.isSortPanelLocationDropdownOpen = false;
    this.onSortPanelLocationIdChange(loc.id);
  }

  shouldHideCategoryTitle(category: FilterCategory): boolean {
    return this.usePackageSortLayout && !this.carWashCategory && category.id === 'sortBy';
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['isVisible'] && changes['isVisible'].currentValue === true) {
      // Reset closing state when modal becomes visible
      this.isClosing = false;
      // The overlay's *ngIf tears the rewash section down on close, so every open remounts
      // it and starts a fresh fetch. Assume it is loading from here rather than waiting for
      // the child to say so, which would land mid-pass and trip ExpressionChangedAfterChecked.
      this.isRewashLoading = this.showRewashSettings;
      this.isFamilyLoading = this.showFamilyPlanSettings;
      this.isFamilyDirty = false;
    }
    if (changes['isVisible'] && changes['isVisible'].currentValue === false) {
      this.closeDropdown();
      this.cancelCustomSortRename();
      this.closeSortPanelLocationDropdown();
    }
  }

  ngOnDestroy(): void {
    this.closeDropdown();
  }

  onClose(): void {
    this.isClosing = true;
    this.closeDropdown();
    this.openCustomSortMenuValue = null;
    this.closeSortPanelLocationDropdown();
    this.cancelCustomSortRename();
    setTimeout(() => {
      this.close.emit();
    }, 200); // Wait for animation to complete
  }

  onMostUsedServicesClick(): void {
    if (this.isSortPanelLocationMissing) {
      return;
    }
    this.openCustomSortMenuValue = null;
    this.closeSortPanelLocationDropdown();
    this.cancelCustomSortRename();
    this.mostUsedServices.emit();
  }

  onMostUsedServicesDefaultsClick(): void {
    if (this.isSortPanelLocationMissing) {
      return;
    }
    this.openCustomSortMenuValue = null;
    this.closeSortPanelLocationDropdown();
    this.cancelCustomSortRename();
    this.mostUsedServicesDefaults.emit();
  }

  onCustomDragQuickAction(event: MouseEvent): void {
    event.preventDefault();
    event.stopPropagation();
    if (this.isSortPanelLocationMissing) {
      return;
    }
    this.openCustomSortMenuValue = null;
    this.closeSortPanelLocationDropdown();
    this.cancelCustomSortRename();
    this.mostUsedServices.emit();
  }

  onApply(): void {
    // The body is hidden until every section has loaded, so acting on it before then would
    // apply state the user cannot see. The buttons are disabled too; this guards the
    // keyboard and programmatic paths.
    if (!this.isPanelReady) {
      return;
    }
    this.closeDropdown();
    this.openCustomSortMenuValue = null;
    this.closeSortPanelLocationDropdown();
    this.cancelCustomSortRename();
    const family = this.isFamilyDirty ? this.familyPlanSettings : undefined;
    if (!family) {
      this.apply.emit(this.categories);
      // Let the parent component decide when to close the modal
      return;
    }
    // Save the Family Plan section first; a failed or invalid save keeps the modal open.
    family.save().subscribe(saved => {
      if (saved) {
        this.apply.emit(this.categories);
      }
    });
  }

  onClear(): void {
    if (!this.isPanelReady) {
      return;
    }
    // Reset all selections
    this.categories.forEach(category => {
      category.options.forEach(option => {
        option.selected = false;
      });
    });
    // Close any open dropdowns
    this.closeDropdown();
    this.openCustomSortMenuValue = null;
    this.closeSortPanelLocationDropdown();
    this.cancelCustomSortRename();
    this.emitCustomRange({ startDate: null, endDate: null });
    this.emitAmountRange({ minAmount: null, maxAmount: null });
    this.clear.emit();
  }

  cancelCustomSortRename(): void {
    this.renameEditingOptionValue = null;
    this.renameDraft = '';
    this.renameInlineError = '';
  }

  isCustomSortOption(option: FilterOption): boolean {
    return option.value.startsWith('custom-order:');
  }

  private getCustomSortOrderId(value: string): string | null {
    if (!value.startsWith('custom-order:')) {
      return null;
    }
    const id = value.slice('custom-order:'.length).trim();
    if (!id || id.startsWith('idx:')) {
      return null;
    }
    return id;
  }

  onCustomSortMenuToggle(optionValue: string, event: MouseEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.openCustomSortMenuValue =
      this.openCustomSortMenuValue === optionValue ? null : optionValue;
  }

  onCustomSortRename(option: FilterOption, event: MouseEvent): void {
    event.preventDefault();
    event.stopPropagation();
    const sortOrderId = this.getCustomSortOrderId(option.value);
    if (!sortOrderId) {
      return;
    }
    this.openCustomSortMenuValue = null;
    this.renameInlineError = '';
    this.renameEditingOptionValue = option.value;
    this.renameDraft = option.label || '';
  }

  commitCustomSortRename(option: FilterOption, event?: Event): void {
    event?.preventDefault();
    event?.stopPropagation();
    const sortOrderId = this.getCustomSortOrderId(option.value);
    if (!sortOrderId || this.renameEditingOptionValue !== option.value) {
      return;
    }
    const nextName = this.renameDraft.trim();
    if (!nextName) {
      this.renameInlineError = 'Please enter a valid name.';
      return;
    }
    this.renameInlineError = '';
    this.customSortAction.emit({
      action: 'rename',
      sortOrderId,
      label: option.label,
      newLabel: nextName,
    });
    this.cancelCustomSortRename();
  }

  onCustomSortDelete(option: FilterOption, event: MouseEvent): void {
    event.preventDefault();
    event.stopPropagation();
    const sortOrderId = this.getCustomSortOrderId(option.value);
    if (!sortOrderId) {
      return;
    }
    this.openCustomSortMenuValue = null;
    this.customSortAction.emit({ action: 'delete', sortOrderId, label: option.label });
  }

  onOptionChange(categoryId: string, optionId: string): void {
    this.openCustomSortMenuValue = null;
    this.cancelCustomSortRename();
    const category = this.categories.find(cat => cat.id === categoryId);
    if (category) {
      // For radio button groups, only one option can be selected at a time
      if (category.type === 'radio') {
        const option = category.options.find(opt => opt.id === optionId);

        // Special case: selecting "Custom (Drag & Drop)" opens arrange modal directly.
        if (
          option &&
          this.showMostUsedServices &&
          this.triggerMostUsedOnOptionValue &&
          category.id === 'sortBy' &&
          option.value === this.triggerMostUsedOnOptionValue
        ) {
          this.mostUsedServices.emit();
          return;
        }

        // Clear all selections first
        category.options.forEach(opt => (opt.selected = false));
        // Set the selected option
        if (option) {
          option.selected = true;
        }
      } else {
        // For checkboxes, toggle the selection
        const option = category.options.find(opt => opt.id === optionId);
        if (option) {
          option.selected = !option.selected;
        }
      }
      this.optionChange.emit({ categoryId, optionId, categories: this.categories });
    }
  }

  onDropdownChange(categoryId: string, event: Event): void {
    const selectElement = event.target as HTMLSelectElement;
    const selectedValue = selectElement.value;

    const category = this.categories.find(cat => cat.id === categoryId);
    if (category) {
      // Clear all selections first
      category.options.forEach(opt => (opt.selected = false));

      // Set the selected option
      const option = category.options.find(opt => opt.value === selectedValue);
      if (option) {
        option.selected = true;
      }
    }
  }

  getSelectedCount(): number {
    const optionCount = this.categories.reduce((total, category) => {
      // Range categories carry no options; their state lives in the *RangeConfig inputs and is
      // counted separately below.
      if (category.type === 'amount-range' || category.type === 'date-range') {
        return total;
      }
      const selected = category.options.filter(option => {
        if (!option.selected) return false;
        // "All" on Discount Applied is the default (no restriction) — don't count it.
        if (category.id === 'discountApplied' && option.value === 'all') {
          return false;
        }
        // "Custom (Drag & Drop)" opens another modal; don't treat it as an applied filter.
        if (
          this.showMostUsedServices &&
          this.triggerMostUsedOnOptionValue &&
          category.id === 'sortBy' &&
          option.value === this.triggerMostUsedOnOptionValue
        ) {
          return false;
        }
        return true;
      }).length;
      return total + selected;
    }, 0);

    const hasAmountRange =
      this.amountRangeConfig &&
      (this.amountRangeConfig.minAmount !== null || this.amountRangeConfig.maxAmount !== null);

    // Without this a date-only selection left Apply/Clear disabled: the pickers are not options,
    // so nothing in the reduce above ever saw them.
    const hasDateRange =
      this.customRangeConfig &&
      (this.customRangeConfig.startDate !== null || this.customRangeConfig.endDate !== null);

    return optionCount + (hasAmountRange ? 1 : 0) + (hasDateRange ? 1 : 0);
  }

  getSelectedDropdownValue(category: FilterCategory): string {
    const selectedOption = category.options.find(opt => opt.selected);
    return selectedOption ? selectedOption.value : '';
  }

  getSelectedDropdownLabel(category: FilterCategory): string {
    const selectedOption = category.options.find(opt => opt.selected);
    if (selectedOption) {
      return selectedOption.label;
    }
    if (category.id === 'discountApplied') {
      return 'All';
    }
    return 'Select a period';
  }

  toggleDropdown(categoryId: string, event: Event): void {
    event.stopPropagation();
    if (this.openDropdownId === categoryId) {
      this.closeDropdown();
      return;
    }
    this.openFilterDropdown(categoryId);
  }

  closeDropdown(): void {
    this.teardownDropdownOverlay();
    DropdownToggleHelper.close(this.filterDropdownOverlayId);
    this.openDropdownId = null;
    this.dropdownOverlayReady = false;
  }

  selectDropdownOption(categoryId: string, optionValue: string, _optionLabel: string): void {
    const category = this.categories.find(cat => cat.id === categoryId);
    if (category) {
      // Clear all selections first
      category.options.forEach(opt => (opt.selected = false));

      // If optionValue is not empty, set the selected option
      let optionId = '';
      if (optionValue) {
        const option = category.options.find(opt => opt.value === optionValue);
        if (option) {
          option.selected = true;
          optionId = option.id;
        }
      }
      // If optionValue is empty, all options remain unselected (showing placeholder)
      this.optionChange.emit({ categoryId, optionId, categories: this.categories });
    }
    this.closeDropdown();
  }

  isDropdownOpen(categoryId: string): boolean {
    return this.openDropdownId === categoryId;
  }

  private openFilterDropdown(categoryId: string): void {
    this.teardownDropdownOverlay();
    this.openDropdownId = categoryId;
    this.dropdownOverlayReady = false;

    const wrapper = this.findDropdownWrapper(categoryId);
    DropdownToggleHelper.open(
      this.filterDropdownOverlayId,
      () => this.closeDropdown(),
      wrapper ?? undefined
    );

    // Match checkbox-multi-dropdown: wait a tick so `.show` is painted before measuring.
    setTimeout(() => {
      if (this.openDropdownId !== categoryId) {
        return;
      }
      this.applyDropdownMenuPosition(categoryId);
      requestAnimationFrame(() => this.applyDropdownMenuPosition(categoryId));
      this.bindOverlayRepositionListeners();
    }, 0);
  }

  private applyDropdownMenuPosition(categoryId: string): void {
    if (this.openDropdownId !== categoryId || typeof window === 'undefined') {
      return;
    }

    const wrapper = this.findDropdownWrapper(categoryId);
    const trigger = wrapper?.querySelector('.dropdown-selected') as HTMLElement | null;
    const menu =
      this.overlayMenuEl ??
      (wrapper?.querySelector('.filter-modal-dropdown-menu') as HTMLElement | null);
    if (!trigger || !menu) {
      return;
    }

    this.attachDropdownMenuToBody(menu);

    const rect = trigger.getBoundingClientRect();
    const menuWidth = Math.max(menu.offsetWidth || rect.width, rect.width);
    const menuHeight = menu.offsetHeight || 240;
    const gap = 4;
    const viewportPadding = 8;

    let top = rect.bottom + gap;
    if (top + menuHeight > window.innerHeight - viewportPadding) {
      top = Math.max(viewportPadding, rect.top - menuHeight - gap);
    }

    let left = rect.left;
    left = Math.min(
      Math.max(viewportPadding, left),
      window.innerWidth - menuWidth - viewportPadding
    );

    menu.style.position = 'fixed';
    menu.style.top = `${top}px`;
    menu.style.left = `${left}px`;
    menu.style.right = 'auto';
    menu.style.width = `${rect.width}px`;
    menu.style.minWidth = `${rect.width}px`;
    menu.style.zIndex = '10050';
    menu.style.transform = 'none';
    menu.style.visibility = 'visible';
    menu.style.pointerEvents = 'auto';
    menu.style.display = 'block';
    menu.style.opacity = '1';
    this.dropdownOverlayReady = true;
    DropdownToggleHelper.setActivePanel(this.filterDropdownOverlayId, menu);
    // Keep portaled if CD re-inserts under a transformed/overflow parent.
    this.attachDropdownMenuToBody(menu);
  }

  private attachDropdownMenuToBody(menu: HTMLElement): void {
    if (typeof document === 'undefined' || menu.parentElement === document.body) {
      this.overlayMenuEl = menu;
      DropdownToggleHelper.setActivePanel(this.filterDropdownOverlayId, menu);
      return;
    }
    this.menuHomeParent = menu.parentElement;
    this.menuHomeNextSibling = menu.nextSibling;
    document.body.appendChild(menu);
    this.overlayMenuEl = menu;
    DropdownToggleHelper.setActivePanel(this.filterDropdownOverlayId, menu);
  }

  private teardownDropdownOverlay(): void {
    this.unbindOverlayRepositionListeners();
    this.clearDropdownMenuFixedStyles();
    this.restoreDropdownMenuHome();
    this.dropdownOverlayReady = false;
  }

  private restoreDropdownMenuHome(): void {
    const menu = this.overlayMenuEl;
    if (!menu || !this.menuHomeParent) {
      this.overlayMenuEl = null;
      this.menuHomeParent = null;
      this.menuHomeNextSibling = null;
      return;
    }
    if (menu.parentElement === document.body) {
      this.menuHomeParent.insertBefore(menu, this.menuHomeNextSibling);
    }
    this.overlayMenuEl = null;
    this.menuHomeParent = null;
    this.menuHomeNextSibling = null;
  }

  private clearDropdownMenuFixedStyles(): void {
    const menu = this.overlayMenuEl;
    if (!menu) {
      return;
    }
    menu.style.position = '';
    menu.style.top = '';
    menu.style.left = '';
    menu.style.right = '';
    menu.style.width = '';
    menu.style.minWidth = '';
    menu.style.zIndex = '';
    menu.style.transform = '';
    menu.style.visibility = '';
    menu.style.pointerEvents = '';
    menu.style.display = '';
    menu.style.opacity = '';
  }

  private findDropdownWrapper(categoryId: string): HTMLElement | null {
    return this.host.nativeElement.querySelector(
      `[data-filter-dropdown-id="${categoryId}"]`
    ) as HTMLElement | null;
  }

  private bindOverlayRepositionListeners(): void {
    if (typeof window === 'undefined') {
      return;
    }
    this.unbindOverlayRepositionListeners();
    window.addEventListener('resize', this.onOverlayReposition);
    const wrapper = this.openDropdownId ? this.findDropdownWrapper(this.openDropdownId) : null;
    const scrollParents = this.collectScrollParents(wrapper ?? this.host.nativeElement);
    this.overlayScrollParents = scrollParents;
    for (const parent of scrollParents) {
      parent.addEventListener('scroll', this.onOverlayReposition, true);
    }
  }

  private unbindOverlayRepositionListeners(): void {
    if (typeof window === 'undefined') {
      return;
    }
    window.removeEventListener('resize', this.onOverlayReposition);
    for (const parent of this.overlayScrollParents) {
      parent.removeEventListener('scroll', this.onOverlayReposition, true);
    }
    this.overlayScrollParents = [];
  }

  private collectScrollParents(element: HTMLElement): EventTarget[] {
    const parents: EventTarget[] = [window];
    let node: HTMLElement | null = element.parentElement;
    while (node) {
      const style = window.getComputedStyle(node);
      const overflowValues = `${style.overflow}|${style.overflowY}|${style.overflowX}`;
      if (/(auto|scroll|overlay)/.test(overflowValues)) {
        parents.push(node);
      }
      node = node.parentElement;
    }
    return parents;
  }

  /**
   * Map FilterOption rows to CheckboxMultiDropdownOption (`id` + `name`), same
   * shape as Create Discount Location.
   */
  getMultiDropdownOptions(category: FilterCategory): CheckboxMultiDropdownOption[] {
    return category.options
      .filter(option => Boolean(String(option.value ?? '').trim()))
      .map(option => ({
        id: option.value,
        name: String(option.label ?? '').trim() || option.value,
      }));
  }

  getMultiDropdownSelectedIds(category: FilterCategory): string[] {
    return category.options.filter(option => option.selected).map(option => option.value);
  }

  getMultiDropdownPlaceholder(category: FilterCategory): string {
    return `Select ${category.name}`;
  }

  onMultiDropdownSelectedIdsChange(categoryId: string, selectedIds: string[]): void {
    const category = this.categories.find(cat => cat.id === categoryId);
    if (!category || category.type !== 'multi-dropdown') {
      return;
    }
    const selected = new Set(selectedIds);
    category.options.forEach(option => {
      option.selected = selected.has(option.value);
    });
    this.optionChange.emit({
      categoryId,
      optionId: '',
      categories: this.categories,
    });
  }

  onMultiDropdownClear(categoryId: string): void {
    this.onMultiDropdownSelectedIdsChange(categoryId, []);
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event): void {
    const target = event.target as HTMLElement;
    const dropdownElement = target.closest('.custom-filter-dropdown');
    const dropdownMenu = target.closest('.filter-modal-dropdown-menu');
    const customSortMenu = target.closest('.filter-custom-sort-actions');
    const sortPanelLocationEl = target.closest('.filter-sort-panel-location');

    // Close dropdown if clicking outside (include body-portaled menu panel)
    if (!dropdownElement && !dropdownMenu && this.openDropdownId) {
      this.closeDropdown();
    }
    if (!sortPanelLocationEl && this.isSortPanelLocationDropdownOpen) {
      this.closeSortPanelLocationDropdown();
    }
    if (!customSortMenu && this.openCustomSortMenuValue) {
      this.openCustomSortMenuValue = null;
    }
    const inRename = target.closest('.filter-custom-sort-rename-editor');
    const inCustomSortActions = target.closest('.filter-custom-sort-actions');
    if (this.renameEditingOptionValue && !inRename && !inCustomSortActions) {
      this.cancelCustomSortRename();
    }
  }

  onModalRootClick(event: MouseEvent): void {
    event.stopPropagation();
    const target = event.target as HTMLElement;
    if (!target.closest('.filter-sort-panel-location') && this.isSortPanelLocationDropdownOpen) {
      this.closeSortPanelLocationDropdown();
    }
    if (!target.closest('.filter-custom-sort-actions') && this.openCustomSortMenuValue) {
      this.openCustomSortMenuValue = null;
    }
    const inRename = target.closest('.filter-custom-sort-rename-editor');
    const inCustomSortActions = target.closest('.filter-custom-sort-actions');
    if (this.renameEditingOptionValue && !inRename && !inCustomSortActions) {
      this.cancelCustomSortRename();
    }
  }

  shouldShowCustomRange(category: FilterCategory): boolean {
    if (!this.customRangeConfig) {
      return false;
    }
    const triggerValue = this.customRangeConfig.triggerValue || 'custom';
    if (category.id !== this.customRangeConfig.categoryId) {
      return false;
    }

    // For dropdown type, check selected dropdown value
    if (category.type === 'dropdown') {
      return this.getSelectedDropdownValue(category).toLowerCase() === triggerValue.toLowerCase();
    }

    // For radio type, check selected radio option
    if (category.type === 'radio') {
      const selectedOption = category.options.find(opt => opt.selected);
      return selectedOption
        ? selectedOption.value.toLowerCase() === triggerValue.toLowerCase()
        : false;
    }

    return false;
  }

  onCustomRangeInput(type: 'start' | 'end', value: Date | string | null): void {
    if (!this.customRangeConfig) {
      return;
    }
    const parsedValue = value ? new Date(value) : null;
    const nextRange: DateRange = {
      startDate: type === 'start' ? parsedValue : this.customRangeConfig.startDate,
      endDate: type === 'end' ? parsedValue : this.customRangeConfig.endDate,
    };
    // Update the config to keep UI in sync
    this.customRangeConfig.startDate = nextRange.startDate;
    this.customRangeConfig.endDate = nextRange.endDate;
    this.emitCustomRange(nextRange);
  }

  private emitCustomRange(range: DateRange): void {
    if (this.customRangeConfig) {
      this.customRangeChange.emit(range);
    }
  }

  /**
   * A `date-range` category renders its two pickers unconditionally, the same way
   * `amount-range` does — there is no preset radio gating them.
   *
   * This is distinct from the custom range shown under a radio/dropdown, which only appears
   * once the user picks the "Custom" option.
   */
  /**
   * Exactly one end of the date range filled in.
   *
   * A one-sided range cannot be applied — the host needs both bounds before it will filter — so
   * Apply is disabled until the pair is complete. Clear stays enabled (it counts through
   * getSelectedCount), otherwise a stray start date would be stuck on screen with no way to
   * remove it.
   */
  hasIncompleteDateRange(): boolean {
    if (!this.customRangeConfig) {
      return false;
    }
    const { startDate, endDate } = this.customRangeConfig;
    return Boolean(startDate) !== Boolean(endDate);
  }

  shouldShowDateRange(category: FilterCategory): boolean {
    return (
      !!this.customRangeConfig &&
      category.type === 'date-range' &&
      category.id === this.customRangeConfig.categoryId
    );
  }

  shouldShowAmountRange(category: FilterCategory): boolean {
    return (
      !!this.amountRangeConfig &&
      category.type === 'amount-range' &&
      category.id === this.amountRangeConfig.categoryId
    );
  }

  onAmountRangeInput(type: 'min' | 'max', value: number | null): void {
    if (!this.amountRangeConfig) {
      return;
    }
    const nextRange: FilterModalAmountRange = {
      minAmount: type === 'min' ? value : this.amountRangeConfig.minAmount,
      maxAmount: type === 'max' ? value : this.amountRangeConfig.maxAmount,
    };
    this.amountRangeConfig.minAmount = nextRange.minAmount;
    this.amountRangeConfig.maxAmount = nextRange.maxAmount;
    this.emitAmountRange(nextRange);
  }

  private emitAmountRange(range: FilterModalAmountRange): void {
    if (this.amountRangeConfig) {
      this.amountRangeChange.emit(range);
    }
  }

  @HostListener('document:keydown', ['$event'])
  onDocumentKeydown(ev: KeyboardEvent): void {
    if (!this.isVisible || !this.renameEditingOptionValue) {
      return;
    }
    if (ev.key === 'Escape') {
      ev.preventDefault();
      this.cancelCustomSortRename();
    }
  }
}
